import { DealerModel } from '../models/dealer.model'

/**
 * Brand name to GM make code, as accepted by the dealer API's `makeCodes` parameter and
 * returned on each dealer record. Shared by every component that either queries the dealer
 * API by brand or reports a dealer's brands to analytics.
 */
export const BRAND_MAKE_CODES = {
	chevrolet: '001',
	cadillac: '006',
	gmc: '012',
	buick: '004',
} as const

const MAKE_CODE_BRANDS: Record<string, string> = Object.fromEntries(
	Object.entries(BRAND_MAKE_CODES).map(([brand, makeCode]) => [makeCode, brand])
)

/**
 * A single dealer as GBAAL expects it — exactly the seven keys its dealer schema allows, each
 * `dealer`-prefixed as the schema names them. `dealerBac` is the only required field; every
 * other key is omitted rather than emitted as `null`/empty when the dealer record doesn't
 * supply it.
 */
export interface GbaalDealer {
	dealerBac: string
	dealerName?: string
	dealerBrands?: string[]
	dealerCountry?: string
	dealerState?: string
	dealerCity?: string
	dealerZipCode?: string
}

/**
 * Publisher-supplied `link` fields to write alongside the dealer data. GBAAL derives
 * `link.value` from the interacted element's text content, so this is only needed when that
 * text would be a poor label for the interaction.
 */
export interface GbaalLinkData {
	value: string
}

/**
 * Map a dealer API record onto the fields GBAAL's dealer schema accepts. Deliberately an
 * explicit allowlist rather than a spread of the API record: the schema is strict, so a single
 * carried-over key (`distance`, `phone`, `dealerUrl`, …) or a dealer missing its `dealerBac`
 * invalidates the whole dealers array and GBAAL drops all of it — silently, without recording
 * an error. Values are passed through as the API returns them; GBAAL's schema trims and
 * lowercases them.
 *
 * Shared by the `dealerSearch` event payload and the `data-gbaal` markup attribute so the two
 * can't report different fields for the same dealer.
 *
 * @param apiDealer - Dealer record as returned by the dealer API.
 */
export function toGbaalDealer(apiDealer: DealerModel): GbaalDealer {
	const brands = mapMakeCodesToBrands(apiDealer.makeCodes)

	return {
		dealerBac: apiDealer.bac,
		...(apiDealer.dealerName ? { dealerName: apiDealer.dealerName } : {}),
		...(brands.length ? { dealerBrands: brands } : {}),
		...(apiDealer.address?.countryIso ? { dealerCountry: apiDealer.address.countryIso } : {}),
		...(apiDealer.address?.countrySubdivisionCode ? { dealerState: apiDealer.address.countrySubdivisionCode } : {}),
		...(apiDealer.address?.cityName ? { dealerCity: apiDealer.address.cityName } : {}),
		...(apiDealer.address?.postalCode ? { dealerZipCode: apiDealer.address.postalCode } : {}),
	}
}

/**
 * Write the `data-gbaal` dealer data attribute onto an element. GBAAL resolves the data for an
 * interaction from a single `closest('[data-gbaal]')` and never merges JSON across ancestors,
 * so one attribute on a container supplies the dealer data for every interaction inside it —
 * but any descendant that needs its own attribute must repeat the dealer data too.
 *
 * Set via `setAttribute` rather than interpolated into markup so the JSON needs no escaping.
 *
 * Callers routinely pass a `querySelector` result, so a missing element is a no-op: losing the
 * analytics for one interaction is preferable to throwing part-way through rendering a list.
 *
 * @param el - Element covering the interactions this dealer data applies to.
 * @param dealers - Dealer(s) the covered interactions relate to.
 * @param link - `link` fields to publish alongside the dealers; these override GBAAL's own.
 */
export function setGbaalDealerAttr(el: Element | null, dealers: DealerModel | DealerModel[], link?: GbaalLinkData): void {
	if (!el) {
		return
	}

	const dealerList = Array.isArray(dealers) ? dealers : [dealers]

	el.setAttribute('data-gbaal', JSON.stringify({
		...(link ? { link } : {}),
		dealers: dealerList.map(dealer => toGbaalDealer(dealer)),
	}))
}

/**
 * Resolve a dealer's make codes to brand names, dropping codes with no known brand and
 * collapsing the duplicates the dealer API routinely returns (e.g. `['001', '001']`).
 *
 * @param makeCodes - Make codes from the dealer record.
 */
function mapMakeCodesToBrands(makeCodes: string[] = []): string[] {
	return makeCodes.reduce((brands: string[], makeCode) => {
		const brand = MAKE_CODE_BRANDS[makeCode]

		if (brand && !brands.includes(brand)) {
			brands.push(brand)
		}

		return brands
	}, [])
}
